import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import type { PromotionDto } from "@/features/fee-engine/api/dto";
import { PromotionRowActions } from "@/features/fee-engine/components/promotions/promotion-row-actions";
import {
  PROMOTION_STATUSES,
  type PromotionStatus,
} from "@/features/shared/api/enums";

export type PromotionRow = PromotionDto & { id: string };

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

const STATUS_VARIANTS: Record<
  PromotionStatus,
  "warning" | "success" | "destructive" | "secondary"
> = {
  DRAFT: "warning",
  ACTIVE: "success",
  EXPIRED: "secondary",
  DISABLED: "destructive",
};

/** Colonnes de la liste des promotions (`POST /Promotions/all`). */
export const promotionsColumns: ColumnsClarFactory<PromotionRow> = (navigate) => [
  {
    id: "code",
    accessorKey: "code",
    header: t("fees.promotions.columns.code"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono font-semibold">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "description",
    accessorKey: "description",
    header: t("fees.promotions.columns.description"),
    cell: ({ getValue }) => (
      <span className="text-sm text-muted-foreground">
        {(getValue() as string) || "—"}
      </span>
    ),
    meta: { type: "denied" },
  },
  {
    id: "discountType",
    accessorKey: "discountType",
    header: t("fees.promotions.columns.discountType"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="secondary">{getValue() as string}</Badge>
      </NoTranslate>
    ),
    // Pas dans PromotionFilter.
    meta: { type: "denied" },
  },
  {
    id: "value",
    accessorKey: "value",
    header: t("fees.promotions.columns.value"),
    cell: ({ getValue, row }) => {
      const raw = getValue() as number | undefined;
      if (raw == null) return "—";
      const suffix =
        row.original.discountType === "PERCENT_OFF_FEE" ? "%" : "";
      return (
        <NoTranslate as="span" className="font-medium">
          {raw}
          {suffix}
        </NoTranslate>
      );
    },
    meta: { type: "denied" },
  },
  {
    id: "productCode",
    accessorKey: "productCode",
    header: t("fees.promotions.columns.productCode"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("fees.promotions.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as PromotionStatus | undefined;
      return status ? (
        <NoTranslate as="span">
          <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>{status}</Badge>
        </NoTranslate>
      ) : (
        "—"
      );
    },
    meta: { type: "select", options: selectOptions(PROMOTION_STATUSES) },
  },
  {
    id: "validFrom",
    accessorKey: "validFrom",
    header: t("fees.promotions.columns.validFrom"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "validTo",
    accessorKey: "validTo",
    header: t("fees.promotions.columns.validTo"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <PromotionRowActions promotion={row.original} navigate={navigate} />
      ) : null,
    meta: { type: "denied" },
  },
];
